/**
 * RootErrorBoundary — React error boundary that catches all unhandled errors
 * in the component tree, including React Router 7 loader/action errors that
 * don't have a dedicated `errorElement`.
 *
 * Renders:
 *   - NotFoundPage for 404 RouteErrorResponse
 *   - ErrorPage for 5xx RouteErrorResponse
 *   - ErrorPage as fallback for any other error
 *
 * Wrapping: AppRouter wraps RouterProvider in this boundary so any
 * uncaught error (loader throw, render crash) shows our styled page
 * instead of the browser's blank error screen.
 *
 * @layer-1 — shared component
 */
import { Component, type ReactNode } from "react";
import { isRouteErrorResponse } from "react-router";
import { NotFoundPage } from "@/features/errors/components/NotFoundPage";
import { ErrorPage } from "@/features/errors/components/ErrorPage";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: unknown;
}

/**
 * RootErrorBoundary — catches React errors and renders styled error pages.
 *
 * Flow: error thrown → getDerivedStateFromError stores it → render shows error page.
 * Reset by remounting via key change when navigation occurs.
 */
export class RootErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override render() {
    if (this.state.hasError) {
      const error = this.state.error;

      if (isRouteErrorResponse(error)) {
        if (error.status === 404) return <NotFoundPage />;
        if (error.status >= 500) return <ErrorPage />;
      }

      return <ErrorPage />;
    }

    return this.props.children;
  }
}